import { Stethoscope } from "lucide-react";

/**
 * Animated loader displayed while the patient dossier is being queried.
 *
 * A stethoscope sits at the center of a rotating conic-gradient ring, with an
 * orbiting pulse dot and a soft breathing glow — a lightweight, self-contained
 * "the request is being processed" indicator. All animations are scoped via a
 * local <style> block so the component drops in anywhere without touching the
 * Tailwind config.
 */
export function StethoscopeLoader({ size = 40 }: { size?: number }) {
  return (
    <span
      className="steth-loader relative inline-flex items-center justify-center"
      style={{ width: size, height: size }}
      role="status"
      aria-live="polite"
    >
      {/* Soft breathing halo */}
      <span className="steth-halo absolute inset-0 rounded-full bg-primary/20" />

      {/* Rotating conic-gradient orbit ring */}
      <span className="steth-ring absolute inset-0 rounded-full" />

      {/* Orbiting pulse dot */}
      <span className="steth-orbit absolute inset-0">
        <span className="steth-dot absolute left-1/2 top-0 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-primary" />
      </span>

      {/* Center stethoscope, gently breathing */}
      <span className="steth-core relative flex items-center justify-center rounded-full bg-white shadow-sm">
        <Stethoscope
          className="text-primary"
          style={{ width: size * 0.42, height: size * 0.42 }}
          strokeWidth={2.25}
        />
      </span>

      <style>{`
        .steth-ring {
          background: conic-gradient(
            from 0deg,
            rgba(23, 82, 183, 0) 0deg,
            rgba(23, 82, 183, 0.15) 140deg,
            rgba(23, 82, 183, 0.85) 320deg,
            rgba(23, 82, 183, 0) 360deg
          );
          -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px));
          mask: radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px));
          animation: steth-spin 1.1s linear infinite;
        }
        .steth-orbit {
          animation: steth-spin 1.6s linear infinite;
        }
        .steth-core {
          width: 62%;
          height: 62%;
        }
        .steth-halo {
          animation: steth-breathe 1.8s ease-in-out infinite;
        }
        .steth-dot {
          box-shadow: 0 0 6px 1px rgba(23, 82, 183, 0.7);
        }
        @keyframes steth-spin {
          to { transform: rotate(360deg); }
        }
        @keyframes steth-breathe {
          0%, 100% { transform: scale(0.85); opacity: 0.35; }
          50% { transform: scale(1.05); opacity: 0.7; }
        }
        @media (prefers-reduced-motion: reduce) {
          .steth-ring, .steth-orbit, .steth-halo { animation: none; }
        }
      `}</style>
    </span>
  );
}
